import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useStore } from '../store';

// Top-right menu: connect QR, device-GPS toggle, simulator (testing), settings, logout.
// (Map view + locate have their own buttons stacked alongside.)
export function BurgerMenu() {
  const { t } = useTranslation();
  const connectUrl = useStore((s) => s.serverConfig?.connectUrl ?? null);
  const authRequired = useStore((s) => s.serverConfig?.authRequired ?? false);
  const allowIngest = useStore((s) => s.serverConfig?.allowIngest ?? false);
  const gnssSource = useStore((s) => s.serverConfig?.gnssSource ?? 'sim');
  const showSimControls = useStore((s) => s.showSimControls);
  const setShowSimControls = useStore((s) => s.setShowSimControls);
  const simEnabled = useStore((s) => s.simEnabled);
  const setSimEnabled = useStore((s) => s.setSimEnabled);
  const providerActive = useStore((s) => s.providerActive);
  const providerError = useStore((s) => s.providerError);
  const providerAcc = useStore((s) => s.providerAccuracyM);
  const startProvider = useStore((s) => s.startProvider);
  const stopProvider = useStore((s) => s.stopProvider);
  const logout = useStore((s) => s.logout);
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  // On a sim-driven server the sim is always on (local dev). Otherwise it's the browser test sim.
  const serverSim = gnssSource === 'sim';
  const simActive = serverSim || simEnabled;

  // The connect QR only makes sense for on-prem/standalone LAN join, where the server advertises a
  // reachable mDNS URL (`connectUrl`). Hosted/cloud deployments have no LAN URL — no QR.
  const showConnect = connectUrl != null;

  useEffect(() => {
    if (open && showConnect && !qr) {
      QRCode.toDataURL(connectUrl, { margin: 1, width: 180 })
        .then(setQr)
        .catch(() => setQr(null));
    }
  }, [open, qr, connectUrl, showConnect]);

  return (
    <div className="top-safe absolute right-3">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={t('menu.title')}
        className="pointer-events-auto relative flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur active:scale-95"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" />
        </svg>
        {/* At-a-glance badge: this device is feeding GPS. */}
        {providerActive && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-black/40 bg-emerald-400" />
        )}
      </button>

      {open && (
        <div className="pointer-events-auto absolute right-0 top-14 w-64 rounded-2xl bg-zinc-900/95 p-4 text-white shadow-2xl backdrop-blur">
          {showConnect && (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-white/50">
                {t('menu.connect')}
              </h3>
              <p className="mt-1 text-xs text-white/50">{t('menu.connectHint')}</p>
              {qr && (
                <img src={qr} alt="QR" className="mx-auto mt-2 w-40 rounded-lg bg-white p-1" />
              )}
            </>
          )}

          {/* This device as the GPS source (phone/tablet geolocation → server). */}
          {allowIngest && (
            <div className={showConnect ? 'mt-4' : ''}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-white/50">
                {t('menu.deviceGps')}
              </h3>
              <button
                onClick={() => (providerActive ? stopProvider() : startProvider())}
                className={`mt-2 flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm font-medium active:scale-[0.99] ${
                  providerActive ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-white/80'
                }`}
              >
                <span className="flex items-center gap-2">
                  📍 {providerActive ? t('provider.sending') : t('provider.send')}
                </span>
                {providerActive && providerAcc != null && (
                  <span className="tabular-nums text-white/70">±{Math.round(providerAcc)} m</span>
                )}
              </button>
              {providerError && (
                <p className="mt-1 rounded-lg bg-red-600/90 px-3 py-1.5 text-xs text-white">
                  {t(`provider.error.${providerError}`)}
                </p>
              )}
            </div>
          )}

          <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-white/50">
            {t('menu.settings')}
          </h3>

          {/* Enable the browser test simulator (only where the server isn't already sim-driven). */}
          {!serverSim && (
            <label className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-dashed border-fuchsia-400/50 px-3 py-2">
              <span className="flex items-center gap-2 text-sm">
                <span className="rounded bg-fuchsia-600/90 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                  {t('driving.simDev')}
                </span>
                {t('menu.simEnable')}
              </span>
              <input
                type="checkbox"
                checked={simEnabled}
                onChange={(e) => setSimEnabled(e.target.checked)}
                className="h-5 w-5 accent-fuchsia-500"
              />
            </label>
          )}

          {/* Nudge/recenter controls for the active sim (dev-testing aid). */}
          {simActive && (
            <label className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-dashed border-fuchsia-400/50 px-3 py-2">
              <span className="flex items-center gap-2 text-sm">
                <span className="rounded bg-fuchsia-600/90 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                  {t('driving.simDev')}
                </span>
                {t('menu.simControls')}
              </span>
              <input
                type="checkbox"
                checked={showSimControls}
                onChange={(e) => setShowSimControls(e.target.checked)}
                className="h-5 w-5 accent-fuchsia-500"
              />
            </label>
          )}

          {!simActive && !allowIngest && (
            <p className="mt-1 text-xs text-white/40">{t('menu.settingsSoon')}</p>
          )}

          {authRequired && (
            <button
              onClick={logout}
              className="mt-4 w-full rounded-xl bg-zinc-800 py-2.5 text-sm font-medium text-white/80 active:scale-[0.99]"
            >
              {t('auth.logout')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
